import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleApiResponse, getAuthHeaders, setToken } from '../utils/auth';

const API_BASE = import.meta.env.VITE_API_BASE;

const VerificationCodeInput = ({ onCodeComplete }) => {
  const [code, setCode] = useState(['', '', '', '', '']);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef([]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Handle global keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent handling if the target is one of our inputs
      if (e.target.matches('input[type="text"]')) {
        return;
      }

      // Only handle numeric keys and backspace
      if ((/^\d$/.test(e.key) || e.key === 'Backspace') && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (e.key === 'Backspace') {
          // Handle backspace
          const newCode = [...code];
          if (newCode[focusedIndex] === '') {
            // If current position is empty, move back and clear previous
            if (focusedIndex > 0) {
              newCode[focusedIndex - 1] = '';
              setCode(newCode);
              setFocusedIndex(focusedIndex - 1);
              inputRefs.current[focusedIndex - 1]?.focus();
            }
          } else {
            // Clear current position
            newCode[focusedIndex] = '';
            setCode(newCode);
          }
          onCodeComplete('');
        } else {
          // Handle numeric input
          const newCode = [...code];
          newCode[focusedIndex] = e.key;
          setCode(newCode);
          
          if (focusedIndex < 4) {
            setFocusedIndex(focusedIndex + 1);
            inputRefs.current[focusedIndex + 1]?.focus();
          }

          // Check if all digits are filled
          if (newCode.every(digit => digit !== '')) {
            onCodeComplete(newCode.join(''));
          } else {
            onCodeComplete('');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [code, focusedIndex, onCodeComplete]);

  const handleInput = (e, index) => {
    const value = e.target.value.slice(-1);
    if (/^\d$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      
      if (index < 4) {
        setFocusedIndex(index + 1);
        inputRefs.current[index + 1]?.focus();
      }

      // Check if all digits are filled
      if (newCode.every(digit => digit !== '')) {
        onCodeComplete(newCode.join(''));
      } else {
        onCodeComplete('');
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = Math.max(0, index - 1);
      setFocusedIndex(prevIndex);
      inputRefs.current[prevIndex]?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = Math.min(4, index + 1);
      setFocusedIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      const newCode = [...code];
      if (newCode[index] === '' && index > 0) {
        newCode[index - 1] = '';
        setFocusedIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else {
        newCode[index] = '';
      }
      setCode(newCode);
      onCodeComplete('');
    }
  };

  const handleFocus = (index) => {
    setFocusedIndex(index);
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex justify-center gap-4">
      {code.map((digit, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleInput(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onFocus={() => handleFocus(index)}
          onClick={() => handleFocus(index)}
          className={`w-12 h-12 text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] caret-transparent ${
            focusedIndex === index 
              ? 'bg-[var(--input-bg-focus)] border-[var(--primary-color)]' 
              : 'bg-[var(--input-bg)] border-[var(--border-color)]'
          } text-[var(--text-primary)]`}
        />
      ))}
    </div>
  );
};

export default function SMSVerification() {
  const [countryPhone, setCountryPhone] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [regToken, setRegToken] = useState(null);

  useEffect(() => {
    // Get phone information from sessionStorage
    const phoneData = sessionStorage.getItem('phoneVerificationData');
    if (phoneData) {
      try {
        const { PhoneCountry, Phone, regToken } = JSON.parse(phoneData);
        setCountryPhone(PhoneCountry);
        setPhone(Phone);
        if (regToken) {
          setRegToken(regToken);
        }
      } catch (err) {
        console.error('Failed to parse phone data:', err);
        navigate('/login');
      }
    } else {
      // Redirect to login if phone data is not available
      navigate('/login');
    }
  }, [navigate]);

  // Add an effect to set initial cooldown when component mounts
  useEffect(() => {
    setResendCooldown(8);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleResendCode = async () => {
    if (loading || resendCooldown > 0) return;
    
    try {
      setLoading(true);
      setResendCooldown(8); // Start 8-second cooldown
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Use different endpoint based on whether regToken is available
      const endpoint = regToken 
        ? `${API_BASE}/login/${regToken}/resendsms`
        : `${API_BASE}/auth/resendsms`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneCountry: countryPhone,
          phone: phone
        })
      });

      // Check for unauthorized response
      if (res.status === 401) {
        // Clear stored data and redirect to login
        sessionStorage.removeItem('phoneVerificationData');
        navigate('/login', { replace: true });
        return;
      }

      if (!res.ok) {
        setGeneralError('Failed to resend verification code');
      }

    } catch (err) {
      setGeneralError('Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeComplete = (code) => {
    setVerificationCode(code);
    setIsCodeComplete(code.length === 5);
    setError('');
    setGeneralError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 5 || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      // Use different endpoint based on whether regToken is available
      const endpoint = regToken 
        ? `${API_BASE}/login/${regToken}/verifySMS?code=${verificationCode}`
        : `${API_BASE}/auth/verifySMS`;
        
      const method = 'POST';
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      };
      
      const body = regToken ? undefined : JSON.stringify({
        phoneCountry: countryPhone,
        phone: phone,
        code: verificationCode
      });

      const response = await fetch(endpoint, {
        method,
        headers,
        body
      });

      // Check for unauthorized response
      if (response.status === 401) {
        // Clear stored data and redirect to login
        sessionStorage.removeItem('phoneVerificationData');
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify code');
      }

      // Handle JWT token if present
      if (data.jwtToken || data.token) {
        const token = data.jwtToken || data.token;
        setToken(token); // This will trigger the auth-token-changed event
        sessionStorage.removeItem('phoneVerificationData');
        // Let the auth event handler handle the navigation
        return;
      }

      // Handle redirect URL if present (fallback for backward compatibility)
      if (data.redirectUrl) {
        sessionStorage.removeItem('phoneVerificationData');
        navigate(data.redirectUrl, { replace: true });
        return;
      }

    } catch (err) {
      console.error('Failed to verify code:', err);
      setError(err.message || 'Failed to verify code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative z-20">
      <div className="max-w-sm w-full sm:max-w-md p-4 my-8 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-2">
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path 
                  d="M17 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V4C19 2.9 18.1 2 17 2ZM17 18H7V6H17V18Z" 
                  fill="var(--accent-primary)"
                />
                <path 
                  d="M12 19C12.5523 19 13 18.5523 13 18C13 17.4477 12.5523 17 12 17C11.4477 17 11 17.4477 11 18C11 18.5523 11.4477 19 12 19Z" 
                  fill="var(--accent-primary)"
                />
              </svg>
            </div>

            <p className="text-[var(--text-primary)] mb-2">
              Enter the verification code sent to
            </p>
            <p className="text-[var(--text-primary)] font-medium">
              +{countryPhone} {phone}
            </p>
          </div>

          <VerificationCodeInput onCodeComplete={handleCodeComplete} />

          {error && (
            <p className="text-red-500 text-center text-sm">{error}</p>
          )}

          <div className="space-y-4">
            <button
              onClick={handleSubmit}
              disabled={!isCodeComplete || submitting}
              className={`w-full flex justify-center items-center px-4 py-2 font-semibold rounded transition
                ${isCodeComplete 
                  ? 'bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] hover:bg-[var(--hover-dark)]' 
                  : 'bg-[var(--disabled-bg)] text-[var(--text-secondary)]'}`}
            >
              {submitting ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-[var(--btn-text-on-accent)] border-t-transparent rounded-full animate-spin mr-2"></div>
                  Verifying...
                </div>
              ) : (
                'Continue'
              )}
            </button>

            <div className="text-[var(--text-secondary)] text-sm text-center">
              <p>Didn't receive the code?</p>
              <p className="mt-1">
                Check your messages or{' '}
                {resendCooldown > 0 ? (
                  <span className="text-[var(--text-secondary)] opacity-50">
                    Resend SMS
                  </span>
                ) : (
                  <button
                    onClick={handleResendCode}
                    className="text-[var(--accent-primary)] hover:text-[var(--text-primary)] hover:underline font-medium"
                  >
                    Resend SMS
                  </button>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 